/// <reference path="../../setup/_references.ts" />
module onvio.practice.widgets {
    'use strict';

    const POSITIVE_COLOR = '#005DA2';
    const NEGATIVE_COLOR = '#DC0A0A';

    /** Aging data (used within this controller only) */
    interface IAgingData {
        items?: {
            label: string;
            value: number;
            valueFormatted?: string;
            percent?: number;
            lineStyle?: any;
        }[]
    }

    export class BmPracticeWidgetAgings implements IBmPracticeWidgetService {

        /** Flag if the widget is busy */
        public isBusy: boolean = false;

        /** Ensure the invoice widgets refresh together */
        public coalescingServiceArea: string = 'invoices';

        /** Templates for the widget */
        public bodyTemplate: string = 'practice-widgets/agings/agings.html';
        public size: WidgetSize = 'small';

        static $inject = [
            '$q',
            '$translate',
            'csAgingsV1',
            'bmpBigNumberUtils',
            'bmpDateUtils'
        ];
        constructor(
            private $q: ng.IQService,
            private $translate: ng.translate.ITranslateService,
            private dataService: cs.IAgingsV1Service,
            private bigNumberUtils: bmp.globalize.IBigNumberUtils,
            private dateUtils: bmp.globalize.IDateUtils) {
                // Nothing required here
        }

        public loadData(args: IBmPracticeWidgetLoadDataArgs): ng.IPromise<any> {

            let formatDate = (date: string) => this.dateUtils.formatDateFromInvariant(date, 'l');

            // Calculates a positive percentage of the total between 0 and 100
            let calculatePercentage = (value: number, total: number): number => {
                return Math.min(100, Math.max(0, ((value === 0 || total === 0) ? 0 : Math.abs(value) / Math.abs(total) * 100)));
            };

            // This is the maximum number of columns that can be shown
            return this.dataService.get({ clientId: args.contextId }).then((r: cs.IAgingsV1Dto) => {

                let agedWIP = [r.workInProgressAging1, r.workInProgressAging2, r.workInProgressAging3, (+r.workInProgressAging4 + +r.workInProgressAging5).toString()];
                let agedAR = [r.accountsReceivableAging1, r.accountsReceivableAging2, r.accountsReceivableAging3, (+r.accountsReceivableAging4 + +r.accountsReceivableAging5).toString()];
                let periods = [formatDate(r.agingDate1), formatDate(r.agingDate2), formatDate(r.agingDate3), formatDate(r.agingDate4)];

                // Remove any blank periods
                while (periods.length > 1 && !periods[periods.length - 1]) {
                    agedWIP.pop();
                    agedAR.pop();
                    periods.pop();
                }

                // Append the '+' suffix to the last period
                periods[periods.length - 1] += '+';

                let data: IAgingData = { items: [] };
                // Build the items based on the type of data being requested
                switch (args.type) {
                    case 'wip':
                        data.items = agedWIP.map((wipAmount, index) => {
                            return { label: periods[index], value: +wipAmount };
                        }).concat([{ label: this.$translate.instant('Bluemoon.Practice.Widget.Agings.Totals'), value: +r.workInProgressTotal }]);
                        break;
                    case 'ar':
                        data.items = agedAR.map((arAmount, index) => {
                            return { label: periods[index], value: +arAmount };
                        }).concat([{ label: this.$translate.instant('Bluemoon.Practice.Widget.Agings.Totals'), value: +r.accountsReceivableTotal }]);
                        break;
                    default:
                        data.items = [{ label: `type "${args.type}" not recognized`, value: 0 }];
                        break;
                }

                data.items.forEach((item: any) => {
                    // Append the formatted value for each item
                    item.valueFormatted = this.bigNumberUtils.formatDecimal(item.value, 'c');
                    // Calculate the percentage
                    item.percent = calculatePercentage(item.value, data.items[data.items.length - 1].value);
                    // Style the line for the chart
                    item.lineStyle = {
                        height: '3px',
                        width: `${item.percent}%`,
                        'background-color': `${item.value > 0 ? POSITIVE_COLOR : NEGATIVE_COLOR}`
                    };
                });

                return data;

            }).catch(() => {
                return undefined;
            });

        }

    }

    angular.module('bm.practice.widgets.agings',
        ['bento.d3', 'csAgingsV1', 'bmpBigNumberUtils', 'bmpDateUtils'])
        .service('bm-practice-widget-agings', BmPracticeWidgetAgings);
}
